import { TimelineContent } from "@/components/ui/timeline-animation";
import { useRef } from "react";
import { Star, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";

const TestimonialsSection = () => {
  const testimonialRef = useRef<HTMLDivElement>(null);
  const { t, language } = useLanguage();

  const revealVariants = {
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.15,
        duration: 0.5,
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };

  const testimonials = [
    {
      name: "Viviana Velasco",
      role: { en: "Estate Planning Client", es: "Cliente de Planificación Patrimonial", vi: "Khách hàng Lập kế hoạch Tài sản" },
      text: { 
        en: "I contacted attorney Daniel Albert for an Estate planning consultation. He was very professional and knowledgeable.",
        es: "Contacté al abogado Daniel Albert para una consulta de planificación patrimonial. Fue muy profesional y conocedor.",
        vi: "Tôi đã liên hệ với luật sư Daniel Albert để tư vấn lập kế hoạch tài sản. Ông ấy rất chuyên nghiệp và hiểu biết."
      },
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=100&auto=format&fit=crop",
      bgClass: "bg-gradient-gold-shine text-foreground"
    },
    {
      name: "Dania Galvan",
      role: { en: "Immigration Client", es: "Cliente de Inmigración", vi: "Khách hàng Di trú" },
      text: { 
        en: "Excellent attorney, speaks the truth and doesn't take your money. 100% recommended.",
        es: "Excelente abogado, habla con la verdad y no te quita tu dinero. 100% recomendado.",
        vi: "Luật sư xuất sắc, nói thật và không lấy tiền của bạn. Giới thiệu 100%."
      },
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=100&auto=format&fit=crop",
      bgClass: "bg-primary text-primary-foreground"
    },
    {
      name: "Isabel Vazquez",
      role: { en: "Immigration Client", es: "Cliente de Inmigración", vi: "Khách hàng Di trú" },
      text: { 
        en: "One of the best lawyers I've ever known. I recommend him 100%.",
        es: "Uno de los mejores abogados que he conocido. Lo recomiendo 100%.",
        vi: "Một trong những luật sư tốt nhất tôi từng biết. Tôi giới thiệu 100%."
      },
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=100&auto=format&fit=crop",
      bgClass: "bg-card text-card-foreground border-2 border-primary"
    },
    {
      name: "Alberto Rios",
      role: { en: "Criminal Defense Client", es: "Cliente de Defensa Criminal", vi: "Khách hàng Bào chữa Hình sự" },
      text: { 
        en: "Mr. Albert gave me the confidence to fight my case! Great staff as well! Highly recommend!",
        es: "¡El Sr. Albert me dio la confianza para luchar en mi caso! ¡Excelente personal también! ¡Altamente recomendado!",
        vi: "Ông Albert đã cho tôi niềm tin để chiến đấu trong vụ án của mình! Nhân viên cũng tuyệt vời! Rất đề xuất!"
      },
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=100&auto=format&fit=crop",
      bgClass: "bg-card text-card-foreground border-2 border-accent"
    },
    {
      name: "Bincy Varghese",
      role: { en: "Legal Consultation", es: "Consulta Legal", vi: "Tư vấn Pháp lý" },
      text: { 
        en: "He was extremely helpful and knowledgeable. The staff was extremely kind and courteous. I will definitely recommend Daniel Albert law firm.",
        es: "Fue extremadamente útil y conocedor. El personal fue extremadamente amable y cortés. Definitivamente recomendaré el bufete Daniel Albert.",
        vi: "Ông ấy cực kỳ hữu ích và hiểu biết. Nhân viên cực kỳ tử tế và lịch sự. Tôi chắc chắn sẽ giới thiệu văn phòng luật Daniel Albert."
      },
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=100&auto=format&fit=crop",
      bgClass: "bg-card text-card-foreground border-2 border-primary"
    },
    {
      name: "Stephanie Benitez",
      role: { en: "Family Law Client", es: "Cliente de Derecho Familiar", vi: "Khách hàng Luật Gia đình" },
      text: { 
        en: "An outstanding experience with Daniel Albert Law Firm. From the beginning, their team has been helpful and answered all my questions.",
        es: "Una experiencia sobresaliente con el Bufete Daniel Albert. Desde el principio, su equipo fue útil y respondió todas mis preguntas.",
        vi: "Trải nghiệm xuất sắc với Văn phòng Luật Daniel Albert. Ngay từ đầu, đội ngũ của họ đã hữu ích và trả lời tất cả câu hỏi của tôi."
      },
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=100&auto=format&fit=crop",
      bgClass: "bg-card text-card-foreground border-2 border-accent"
    },
    {
      name: "Chema Barrios",
      role: { en: "Immigration Client", es: "Cliente de Inmigración", vi: "Khách hàng Di trú" },
      text: { 
        en: "Very good immigration attorney, I recommend them.",
        es: "Muy buen abogado de inmigración, se los recomiendo.",
        vi: "Luật sư di trú rất tốt, tôi giới thiệu họ."
      },
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=100&auto=format&fit=crop",
      bgClass: "bg-primary text-primary-foreground"
    },
    {
      name: "Jasmin Sagastizado",
      role: { en: "Immigration Client", es: "Cliente de Inmigración", vi: "Khách hàng Di trú" },
      text: { 
        en: "I appreciate the very good service. They helped with my immigration process, they have very friendly and trained staff which facilitated my process to happen in the shortest time possible.",
        es: "Agradezco por muy buen servicio. Ayudaron con mi proceso migratorio, tienen personal muy amable y capacitado lo que facilitó mi proceso para que se diera en el menor tiempo posible.",
        vi: "Tôi đánh giá cao dịch vụ rất tốt. Họ đã giúp đỡ quá trình nhập cư của tôi, họ có nhân viên rất thân thiện và được đào tạo giúp quá trình của tôi diễn ra trong thời gian ngắn nhất có thể."
      },
      image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?q=80&w=100&auto=format&fit=crop",
      bgClass: "bg-gradient-gold-shine text-foreground"
    },
    {
      name: "Wang Tan",
      role: { en: "Immigration Client", es: "Cliente de Inmigración", vi: "Khách hàng Di trú" },
      text: { 
        en: "Great lawyer and very professional. He answered all the questions we have. I definitely will refer to my friends here.",
        es: "Gran abogado y muy profesional. Respondió todas las preguntas que teníamos. Definitivamente lo recomendaré a mis amigos aquí.",
        vi: "Luật sư tuyệt vời và rất chuyên nghiệp. Ông ấy trả lời tất cả các câu hỏi của chúng tôi. Tôi chắc chắn sẽ giới thiệu cho bạn bè ở đây."
      },
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=100&auto=format&fit=crop",
      bgClass: "bg-card text-card-foreground border-2 border-primary"
    }
  ];

  return (
    <section className="relative h-full container mx-auto rounded-lg py-16 bg-background" ref={testimonialRef}>
      {/* Header */}
      <article className="max-w-screen-md mx-auto text-center space-y-4 mb-12">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-6 w-6 fill-accent text-accent" />
            ))}
          </div>
          <span className="text-2xl font-bold">4.8</span>
          <span className="text-muted-foreground">• 650+ reviews</span>
        </div>
        <TimelineContent 
          as="h2" 
          className="text-3xl md:text-4xl font-bold" 
          animationNum={0} 
          customVariants={revealVariants} 
          timelineRef={testimonialRef}
        >
          {t('testimonials.title')}
        </TimelineContent>
        <TimelineContent 
          as="p" 
          className="text-lg text-muted-foreground max-w-2xl mx-auto" 
          animationNum={1} 
          customVariants={revealVariants} 
          timelineRef={testimonialRef}
        >
          {t('testimonials.subtitle')}
        </TimelineContent>
      </article>

      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-4">
        {/* Column 1 */}
        <div className="flex flex-col space-y-4">
          <TimelineContent 
            animationNum={0} 
            customVariants={revealVariants} 
            timelineRef={testimonialRef} 
            className={`flex-[7] flex flex-col justify-between relative overflow-hidden rounded-xl p-6 shadow-lg ${testimonials[0].bgClass}`}
          >
            <article className="flex flex-col h-full justify-between">
              <div className="flex mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-base mb-auto">{testimonials[0].text[language]}</p>
              <div className="flex justify-between items-center pt-6">
                <div>
                  <h3 className="font-semibold text-lg">{testimonials[0].name}</h3>
                  <p className="text-sm opacity-80">{testimonials[0].role[language]}</p>
                </div>
                <img
                  src={testimonials[0].image}
                  alt={testimonials[0].name}
                  className="w-14 h-14 rounded-xl object-cover"
                />
              </div>
            </article>
          </TimelineContent>

          <TimelineContent 
            animationNum={1} 
            customVariants={revealVariants} 
            timelineRef={testimonialRef} 
            className={`flex-[3] flex flex-col justify-between relative overflow-hidden rounded-xl p-6 shadow-lg ${testimonials[1].bgClass}`}
          >
            <article className="flex flex-col h-full justify-between">
              <div className="flex mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-sm mb-auto">{testimonials[1].text[language]}</p>
              <div className="flex justify-between items-center pt-5">
                <div>
                  <h3 className="font-semibold text-base">{testimonials[1].name}</h3>
                  <p className="text-xs opacity-80">{testimonials[1].role[language]}</p>
                </div>
                <img
                  src={testimonials[1].image}
                  alt={testimonials[1].name}
                  className="w-12 h-12 rounded-xl object-cover"
                />
              </div>
            </article>
          </TimelineContent>
        </div>

        {/* Column 2 */}
        <div className="flex flex-col space-y-4">
          <TimelineContent 
            animationNum={2} 
            customVariants={revealVariants} 
            timelineRef={testimonialRef} 
            className={`flex flex-col justify-between relative overflow-hidden rounded-xl p-6 shadow-lg ${testimonials[2].bgClass}`}
          >
            <article className="flex flex-col h-full justify-between">
              <div className="flex mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-sm mb-auto">{testimonials[2].text[language]}</p>
              <div className="flex justify-between items-end pt-5">
                <div>
                  <h3 className="font-semibold text-base">{testimonials[2].name}</h3>
                  <p className="text-xs opacity-80">{testimonials[2].role[language]}</p>
                </div>
                <img
                  src={testimonials[2].image}
                  alt={testimonials[2].name}
                  className="w-12 h-12 rounded-xl object-cover"
                />
              </div>
            </article>
          </TimelineContent>

          <TimelineContent 
            animationNum={3} 
            customVariants={revealVariants} 
            timelineRef={testimonialRef} 
            className={`flex flex-col justify-between relative overflow-hidden rounded-xl p-6 shadow-lg ${testimonials[3].bgClass}`}
          >
            <article className="flex flex-col h-full justify-between">
              <div className="flex mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-sm mb-auto">{testimonials[3].text[language]}</p>
              <div className="flex justify-between items-end pt-5">
                <div>
                  <h3 className="font-semibold text-base">{testimonials[3].name}</h3>
                  <p className="text-xs opacity-80">{testimonials[3].role[language]}</p>
                </div>
                <img
                  src={testimonials[3].image}
                  alt={testimonials[3].name}
                  className="w-12 h-12 rounded-xl object-cover"
                />
              </div>
            </article>
          </TimelineContent>

          <TimelineContent 
            animationNum={4} 
            customVariants={revealVariants} 
            timelineRef={testimonialRef} 
            className={`flex flex-col justify-between relative overflow-hidden rounded-xl p-6 shadow-lg ${testimonials[4].bgClass}`}
          >
            <article className="flex flex-col h-full justify-between">
              <div className="flex mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-sm mb-auto">{testimonials[4].text[language]}</p>
              <div className="flex justify-between items-end pt-5">
                <div>
                  <h3 className="font-semibold text-base">{testimonials[4].name}</h3>
                  <p className="text-xs opacity-80">{testimonials[4].role[language]}</p>
                </div>
                <img
                  src={testimonials[4].image}
                  alt={testimonials[4].name}
                  className="w-12 h-12 rounded-xl object-cover"
                />
              </div>
            </article>
          </TimelineContent>
        </div>

        {/* Column 3 */}
        <div className="flex flex-col space-y-4">
          <TimelineContent 
            animationNum={5} 
            customVariants={revealVariants} 
            timelineRef={testimonialRef} 
            className={`flex-[3] flex flex-col justify-between relative overflow-hidden rounded-xl p-6 shadow-lg ${testimonials[5].bgClass}`}
          >
            <article className="flex flex-col h-full justify-between">
              <div className="flex mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-sm mb-auto">{testimonials[5].text[language]}</p>
              <div className="flex justify-between items-center pt-5">
                <div>
                  <h3 className="font-semibold text-base">{testimonials[5].name}</h3>
                  <p className="text-xs opacity-80">{testimonials[5].role[language]}</p>
                </div>
                <img
                  src={testimonials[5].image}
                  alt={testimonials[5].name}
                  className="w-12 h-12 rounded-xl object-cover"
                />
              </div>
            </article>
          </TimelineContent>

          <TimelineContent 
            animationNum={6} 
            customVariants={revealVariants} 
            timelineRef={testimonialRef} 
            className={`flex-[7] flex flex-col justify-between relative overflow-hidden rounded-xl p-6 shadow-lg ${testimonials[6].bgClass}`}
          >
            <article className="flex flex-col h-full justify-between">
              <div className="flex mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-base mb-auto">{testimonials[6].text[language]}</p>
              <div className="flex justify-between items-center pt-6">
                <div>
                  <h3 className="font-semibold text-lg">{testimonials[6].name}</h3>
                  <p className="text-sm opacity-80">{testimonials[6].role[language]}</p>
                </div>
                <img
                  src={testimonials[6].image}
                  alt={testimonials[6].name}
                  className="w-14 h-14 rounded-xl object-cover"
                />
              </div>
            </article>
          </TimelineContent>
        </div>
      </div>

      {/* View More Button */}
      <div className="text-center mt-12">
        <TimelineContent 
          animationNum={7} 
          customVariants={revealVariants} 
          timelineRef={testimonialRef}
        >
          <Button asChild size="lg" variant="outline" className="border-2 border-primary hover:bg-primary hover:text-primary-foreground">
            <a 
              href="https://share.google/Emqhgt559KN9655jP" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              {t('testimonials.viewMore')}
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </TimelineContent>
      </div>
    </section>
  );
};

export default TestimonialsSection;